import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useFamilyStore } from '../stores/familyStore';
import { 
  hasPermission, 
  getUserRole, 
  hasMinimumRole 
} from '../utils/accessControl';
import type { Permission, FamilyRole } from '../utils/accessControl';

/**
 * Hook do sprawdzania uprawnień użytkownika w kontekście rodziny
 */
export const usePermissions = () => {
  const { user } = useAuthStore();
  const { family } = useFamilyStore();
  const [userRole, setUserRole] = useState<FamilyRole | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const lastFetchRef = useRef<{ userId: string; familyId: string } | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Pobierz rolę użytkownika przy montowaniu komponentu
  useEffect(() => {
    const fetchUserRole = async () => {
      if (!user || !family) {
        setUserRole(null);
        setLoading(false);
        setError(null);
        return;
      }

      // Sprawdź, czy nie pobieramy już tej samej roli
      const currentKey = { userId: user.id, familyId: family.id };
      if (lastFetchRef.current && 
          lastFetchRef.current.userId === currentKey.userId && 
          lastFetchRef.current.familyId === currentKey.familyId) {
        return;
      }

      // Anuluj poprzednie zapytanie
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Utwórz nowy AbortController
      abortControllerRef.current = new AbortController();
      lastFetchRef.current = currentKey;

      setLoading(true);
      setError(null);

      try {
        // 1) Spróbuj wywnioskować rolę lokalnie z already fetched members
        const { members } = useFamilyStore.getState();
        const me = (members || []).find(m => m.user_id === user.id && m.family_id === family.id);
        if (me && me.role) {
          setUserRole(me.role as FamilyRole);
          setError(null);
          return;
        }

        // 2) Fallback do zapytania (bez sztucznego timeoutu)
        const role = await getUserRole(user.id, family.id);
        setUserRole(role);
        setError(null);
      } catch (error: any) {
        console.error('❌ Błąd pobierania roli użytkownika:', error);
        setUserRole(null);
        setError(error.message || 'Błąd pobierania roli użytkownika');
      } finally {
        setLoading(false);
      }
    };

    fetchUserRole();

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [user?.id, family?.id]); // Zmienione zależności na konkretne ID

  /**
   * Sprawdza, czy bieżący użytkownik ma określone uprawnienie
   * @param permission Uprawnienie do sprawdzenia
   * @returns true, jeśli użytkownik ma uprawnienie, false w przeciwnym razie
   */
  const checkPermission = async (permission: Permission): Promise<boolean> => {
    if (!user || !family) {
      return false;
    }
    
    try {
      return await hasPermission(user.id, family.id, permission);
    } catch (error) {
      console.error('❌ Błąd sprawdzania uprawnień:', error);
      return false;
    }
  };

  /**
   * Sprawdza, czy bieżący użytkownik ma rolę o określonym poziomie lub wyższym
   * @param minimumRole Minimalna wymagana rola
   * @returns true, jeśli użytkownik ma wymaganą rolę lub wyższą, false w przeciwnym razie
   */
  const checkMinimumRole = async (minimumRole: FamilyRole): Promise<boolean> => {
    if (!user || !family) {
      return false;
    }
    
    try {
      return await hasMinimumRole(user.id, family.id, minimumRole);
    } catch (error) {
      console.error('❌ Błąd sprawdzania minimalnej roli:', error);
      return false;
    }
  };

  return {
    userRole,
    loading,
    error,
    checkPermission,
    checkMinimumRole,
    isParentA: userRole === 'parent_a',
    isParentB: userRole === 'parent_b',
    isParent: userRole === 'parent_a' || userRole === 'parent_b',
    isObserver: userRole === 'child_viewer',
    
    // Szczegółowe uprawnienia dla UI
    canEditCalendar: userRole === 'parent_a' || userRole === 'parent_b',
    canViewCalendar: true, // wszyscy mogą oglądać
    canEditFamilySettings: userRole === 'parent_a', // tylko administrator
    canViewFamilySettings: userRole === 'parent_a' || userRole === 'parent_b', // tylko rodzice
    canManageChildren: userRole === 'parent_a', // tylko administrator może edytować dzieci
    canViewChildren: userRole === 'parent_a' || userRole === 'parent_b', // rodzice mogą oglądać dzieci
    canManageAccess: userRole === 'parent_a', // tylko administrator
    canChangePassword: true, // wszyscy
    canChangeAppearance: true, // wszyscy
    canViewNotifications: true, // wszyscy (na razie nieaktywne)
    canEditNotifications: false, // na razie nikt
    
    // Uprawnienia do zakładek w ustawieniach
    canAccessGeneralTab: userRole === 'parent_a' || userRole === 'parent_b',
    canAccessChildrenTab: userRole === 'parent_a' || userRole === 'parent_b', // rodzice mogą oglądać
    canAccessAccessTab: userRole === 'parent_a' || userRole === 'parent_b', // rodzice mogą oglądać
    canAccessPasswordTab: true,
    canAccessAppearanceTab: true,
    canAccessNotificationsTab: true
  };
};